/**
 * Utilitaires de calcul de prix et remises partages
 */

/**
 * Resout les prix public et abonne d'une prestation en tenant compte des tarifs specifiques.
 */
export const resolvePrices = (prestation, tarifs) => {
  const tarif = tarifs && prestation?.id ? tarifs[prestation.id] : null;
  const prixPublic =
    tarif && typeof tarif.prix_centimes_public === 'number'
      ? tarif.prix_centimes_public
      : typeof prestation?.prix_centimes_public === 'number'
        ? prestation.prix_centimes_public
        : null;
  const prixAbonne =
    tarif && typeof tarif.prix_centimes_abonne === 'number'
      ? tarif.prix_centimes_abonne
      : typeof prestation?.prix_centimes_abonne === 'number'
        ? prestation.prix_centimes_abonne
        : null;
  return { prixPublic, prixAbonne };
};

const matchesCategoryOffer = (prestation, categoryIds = []) => {
  if (!Array.isArray(categoryIds) || categoryIds.length === 0) return true;

  const prestationCategoryIds = new Set();
  if (prestation?.categorie_id) prestationCategoryIds.add(String(prestation.categorie_id).trim());
  if (prestation?.categorie?.id) prestationCategoryIds.add(String(prestation.categorie.id).trim());
  if (Array.isArray(prestation?.categories)) {
    prestation.categories.forEach((entry) => {
      const id = entry?.categorie?.id || entry?.id || null;
      if (id) prestationCategoryIds.add(String(id).trim());
    });
  }

  return Array.from(prestationCategoryIds).some((id) => categoryIds.includes(id));
};

const distributeFixedTotal = (originalValues = [], fixedTotal) => {
  if (!Array.isArray(originalValues) || originalValues.length === 0) return null;
  if (!Number.isFinite(fixedTotal) || fixedTotal < 0) return null;
  if (originalValues.some((value) => !Number.isFinite(value) || value < 0)) return null;

  const originalTotal = originalValues.reduce((sum, value) => sum + value, 0);
  if (originalTotal <= 0 || fixedTotal >= originalTotal) {
    return originalValues.slice();
  }

  const allocations = originalValues.map((value, index) => {
    const rawAmount = (value / originalTotal) * fixedTotal;
    const amount = Math.min(value, Math.floor(rawAmount));
    return {
      index,
      value,
      amount,
      remainder: rawAmount - Math.floor(rawAmount),
    };
  });

  let allocated = allocations.reduce((sum, entry) => sum + entry.amount, 0);
  let remaining = fixedTotal - allocated;

  allocations.sort((left, right) => {
    if (right.remainder !== left.remainder) return right.remainder - left.remainder;
    if (right.value !== left.value) return right.value - left.value;
    return left.index - right.index;
  });

  while (remaining > 0) {
    let updated = false;
    for (const entry of allocations) {
      if (entry.amount >= entry.value) continue;
      entry.amount += 1;
      remaining -= 1;
      updated = true;
      if (remaining === 0) break;
    }
    if (!updated) break;
  }

  return allocations.sort((left, right) => left.index - right.index).map((entry) => entry.amount);
};

const buildFixedLotPriceMap = ({ prestations = [], tarifs = null, appliedOffers = [] }) => {
  const fixedLotOffers = Array.isArray(appliedOffers)
    ? appliedOffers.filter((offer) => offer.typeOffre === 'lot_prestations_prix_fixe' && offer.active)
    : [];

  const priceMap = new Map();
  if (fixedLotOffers.length === 0) return priceMap;

  const prestationMap = new Map(
    (prestations || []).map((prestation) => [String(prestation?.id || '').trim(), prestation])
  );

  for (const offer of fixedLotOffers) {
    const targetIds = Array.isArray(offer.targetPrestationIds)
      ? Array.from(new Set(offer.targetPrestationIds.map((id) => String(id).trim()).filter(Boolean)))
      : [];
    if (targetIds.length < 2) continue;

    const targetPrestations = targetIds.map((id) => prestationMap.get(id)).filter(Boolean);
    if (targetPrestations.length !== targetIds.length) continue;

    const baseEntries = targetPrestations.map((prestation) => {
      const { prixPublic, prixAbonne } = resolvePrices(prestation, tarifs);
      return {
        id: String(prestation.id),
        prixPublic,
        prixAbonne: prixAbonne !== null ? prixAbonne : prixPublic,
      };
    });

    const originalPublics = baseEntries.map((entry) => entry.prixPublic);
    const originalAbonnes = baseEntries.map((entry) => entry.prixAbonne);
    const fixedPublic = Number(offer.details?.benefice?.prix_public_centimes);
    const fixedAbonne = Number(offer.details?.benefice?.prix_abonne_centimes);
    const distributedPublic = distributeFixedTotal(originalPublics, fixedPublic);
    const distributedAbonne = distributeFixedTotal(originalAbonnes, fixedAbonne);

    baseEntries.forEach((entry, index) => {
      const next = priceMap.get(entry.id) || {};
      if (
        Array.isArray(distributedPublic) &&
        Number.isFinite(distributedPublic[index]) &&
        Number.isFinite(entry.prixPublic) &&
        distributedPublic[index] < entry.prixPublic &&
        next.prixPublic === undefined
      ) {
        next.prixPublic = distributedPublic[index];
      }
      if (
        Array.isArray(distributedAbonne) &&
        Number.isFinite(distributedAbonne[index]) &&
        Number.isFinite(entry.prixAbonne) &&
        distributedAbonne[index] < entry.prixAbonne &&
        next.prixAbonne === undefined
      ) {
        next.prixAbonne = distributedAbonne[index];
      }
      if (next.prixPublic !== undefined || next.prixAbonne !== undefined) {
        priceMap.set(entry.id, next);
      }
    });
  }

  return priceMap;
};

/**
 * Calcule les totaux pour une liste de prestations avec remises et offres.
 */
export const calculateBookingTotals = ({
  prestations = [],
  offeredIds = new Set(),
  tarifs = null,
  appliedOffers = [],
}) => {
  let totalMinutes = 0;
  let totalPrixPublic = 0;
  let totalPrixAbonne = 0;
  let totalOriginalPrixPublic = 0;
  let totalOriginalPrixAbonne = 0;
  let hasPublicPrice = false;
  let hasAbonnePrice = false;
  let hasPublicOriginal = false;
  let hasAbonneOriginal = false;
  let globalDiscountApplied = false;

  const discountOffers = Array.isArray(appliedOffers)
    ? appliedOffers.filter((offer) => offer.typeOffre === 'remise_pourcent_categorie')
    : [];

  const tarifOffers = Array.isArray(appliedOffers)
    ? appliedOffers.filter((offer) => offer.typeOffre === 'tarif_preferentiel_prestation')
    : [];

  const fixedLotPriceMap = buildFixedLotPriceMap({
    prestations,
    tarifs,
    appliedOffers,
  });

  const cheapestOffers = Array.isArray(appliedOffers)
    ? appliedOffers.filter((offer) => offer.typeOffre === 'lot_moins_chere_offerte')
    : [];

  const items = prestations
    .map((prestation) => {
      if (!prestation) return null;

      const duration = parseInt(prestation.duree_min, 10);
      const isOption = Number.isFinite(duration) && duration === 0;

      if (Number.isFinite(duration) && duration > 0) {
        totalMinutes += duration;
      }

      const { prixPublic: basePublic, prixAbonne: baseAbonne } = resolvePrices(prestation, tarifs);
      const originalPublic = basePublic;
      const originalAbonne = baseAbonne !== null ? baseAbonne : basePublic;

      const isFullyOffered = !isOption && (prestation.is_offered || prestation.isOffered || offeredIds.has(String(prestation.id)));

      let prixPublic = isFullyOffered ? 0 : basePublic;
      let prixAbonne = isFullyOffered ? 0 : originalAbonne;
      let isTarifPreferentiel = false;
      let isDiscounted = false;
      let maxDiscountPercent = 0;
      let isOfferedPublic = isFullyOffered;
      let isOfferedAbonne = isFullyOffered;

      const fixedLotPrices = fixedLotPriceMap.get(String(prestation.id));

      if (isFullyOffered) {
        globalDiscountApplied = true;
      }

      if (!isFullyOffered && !isOption && fixedLotPrices) {
        if (Number.isFinite(fixedLotPrices.prixPublic) && Number.isFinite(prixPublic) && fixedLotPrices.prixPublic < prixPublic) {
          prixPublic = fixedLotPrices.prixPublic;
          isTarifPreferentiel = true;
          globalDiscountApplied = true;
        }
        if (Number.isFinite(fixedLotPrices.prixAbonne) && Number.isFinite(prixAbonne) && fixedLotPrices.prixAbonne < prixAbonne) {
          prixAbonne = fixedLotPrices.prixAbonne;
          isTarifPreferentiel = true;
          globalDiscountApplied = true;
        }
      }

      if (!isFullyOffered && !isOption && tarifOffers.length > 0) {
        for (const offer of tarifOffers) {
          const targetIds = offer.targetPrestationIds || offer.details?.conditions?.prestation_ids || [];
          if (!targetIds.includes(String(prestation.id).trim())) continue;

          const benefice = offer.details?.benefice || {};
          if (Number.isFinite(benefice.prix_public_centimes)) {
            prixPublic = benefice.prix_public_centimes;
            isTarifPreferentiel = true;
          }
          if (Number.isFinite(benefice.prix_abonne_centimes)) {
            prixAbonne = benefice.prix_abonne_centimes;
            isTarifPreferentiel = true;
          }
          break;
        }
      }

      if (!isFullyOffered && !isOption && cheapestOffers.length > 0) {
        for (const offer of cheapestOffers) {
          const discountedIds = Array.isArray(offer.discountedPrestationIds)
            ? offer.discountedPrestationIds.map((id) => String(id))
            : [];
          if (!discountedIds.includes(String(prestation.id))) continue;

          const applicationTarif = offer.applicationTarif || offer.details?.benefice?.application_tarif || 'public';
          if (applicationTarif === 'public' || applicationTarif === 'tous') {
            if (Number.isFinite(prixPublic)) {
              prixPublic = 0;
              isOfferedPublic = true;
            }
          }
          if (applicationTarif === 'abonne' || applicationTarif === 'tous') {
            if (Number.isFinite(prixAbonne)) {
              prixAbonne = 0;
              isOfferedAbonne = true;
            }
          }
          globalDiscountApplied = true;
        }
      }

      if (!isFullyOffered && !isOption && discountOffers.length > 0) {
        for (const offer of discountOffers) {
          const percent = parseInt(offer.details?.benefice?.pourcentage, 10) || 0;
          const categoryIds = (offer.eligibleCategoryIds || offer.details?.conditions?.categorie_ids || []).map((id) =>
            String(id).trim()
          );
          if (!matchesCategoryOffer(prestation, categoryIds) || percent <= maxDiscountPercent) continue;

          maxDiscountPercent = percent;
          isDiscounted = percent > 0;
        }

        if (isDiscounted && maxDiscountPercent > 0) {
          if (Number.isFinite(prixPublic)) {
            prixPublic = Math.round((prixPublic * (100 - maxDiscountPercent)) / 100);
          }
          if (Number.isFinite(prixAbonne)) {
            prixAbonne = Math.round((prixAbonne * (100 - maxDiscountPercent)) / 100);
          }
          globalDiscountApplied = true;
        }
      }

      const finalAbonne = prixAbonne;

      if (Number.isFinite(prixPublic)) {
        totalPrixPublic += prixPublic;
        hasPublicPrice = true;
      }

      if (Number.isFinite(finalAbonne)) {
        totalPrixAbonne += finalAbonne;
        hasAbonnePrice = true;
      }

      if (Number.isFinite(originalPublic)) {
        totalOriginalPrixPublic += originalPublic;
        hasPublicOriginal = true;
      }
      if (Number.isFinite(originalAbonne)) {
        totalOriginalPrixAbonne += originalAbonne;
        hasAbonneOriginal = true;
      }

      return {
        ...prestation,
        isOffered: isOfferedPublic && isOfferedAbonne,
        isOfferedPublic,
        isOfferedAbonne,
        isTarifPreferentiel,
        isDiscounted,
        maxDiscountPercent,
        prixFinalPublic: prixPublic,
        prixFinalAbonne: finalAbonne,
        prixOriginalPublic: originalPublic,
        prixOriginalAbonne: originalAbonne,
      };
    })
    .filter(Boolean);

  return {
    items,
    totalMinutes,
    totalPrixPublic: hasPublicPrice ? totalPrixPublic : null,
    totalPrixAbonne: hasAbonnePrice || hasPublicPrice ? totalPrixAbonne : null,
    totalOriginalPrixPublic: hasPublicOriginal ? totalOriginalPrixPublic : null,
    totalOriginalPrixAbonne: hasAbonneOriginal || hasPublicOriginal ? totalOriginalPrixAbonne : null,
    globalDiscountApplied,
  };
};
